import React, { useState, useEffect } from 'react';
import { Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faBoxes, 
  faChartBar, 
  faCog, 
  faSignOutAlt,
  faUser,
  faShieldAlt,
  faTools,
  faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './Sidebar.scss';
import settingsService from '../../services/settingsService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

function Sidebar() {
  const location = useLocation();
  const { currentUser: user, logout } = useAuthContext();
  const { t } = useTranslation();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Carica il logo e il nome dell'azienda dalle impostazioni
    const fetchCompanyData = async () => {
      try {
        if (user) {
          const { data } = await settingsService.get();
          if (data?.logo) {
            setCompanyLogo(data.logo);
          }
          if (data?.companyName) {
            setCompanyName(data.companyName);
          }
          
          // Verifica se l'utente è admin
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsAdmin(userData.role === 'admin');
          }
        }
      } catch (error) {
        console.error('Errore nel caricamento dei dati aziendali:', error);
      }
    };

    fetchCompanyData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Funzione helper per creare un link con tooltip
  const NavLinkWithTooltip = ({ to, icon, text, active }) => (
    <OverlayTrigger
      placement="right"
      overlay={
        <Tooltip className="sidebar-tooltip">
          {text}
        </Tooltip>
      }
      delay={{ show: 200, hide: 0 }}
    >
      <Nav.Link 
        as={Link} 
        to={to} 
        active={active}
        className="sidebar-link"
      >
        <FontAwesomeIcon icon={icon} />
        <span className="sidebar-text">{text}</span>
      </Nav.Link>
    </OverlayTrigger>
  );

  return (
    <Nav className="sidebar flex-column">
      {/* User Profile */}
      <div className="sidebar-header">
        <div className={`user-avatar ${companyLogo ? 'has-logo' : ''}`}>
          {companyLogo ? (
            <img 
              src={companyLogo} 
              alt={companyName || 'Company Logo'} 
              className="company-logo-avatar"
            />
          ) : (
            <FontAwesomeIcon icon={faUser} />
          )}
        </div>
        <div className="user-info">
          <span className="user-email">{user?.email}</span>
          {companyName && <div className="company-name">{companyName}</div>}
        </div>
      </div>

      {/* Navigation Links */}
      <div className="sidebar-links">
        <Nav.Item>
          <NavLinkWithTooltip
            to="/"
            icon={faHome}
            text={t('sidebar.dashboard')}
            active={location.pathname === '/'}
          />
        </Nav.Item>

        <Nav.Item>
          <NavLinkWithTooltip
            to="/preventivo"
            icon={faFileInvoiceDollar}
            text={t('sidebar.preventivo')}
            active={location.pathname === '/preventivo'}
          />
        </Nav.Item>

        <Nav.Item>
          <NavLinkWithTooltip
            to="/inventory"
            icon={faBoxes}
            text={t('sidebar.inventory')}
            active={location.pathname === '/inventory'}
          />
        </Nav.Item>

        <Nav.Item>
          <NavLinkWithTooltip
            to="/statistics"
            icon={faChartBar}
            text={t('sidebar.statistics')}
            active={location.pathname === '/statistics'}
          />
        </Nav.Item>

        <Nav.Item>
          <NavLinkWithTooltip
            to="/tools"
            icon={faTools}
            text={t('sidebar.tools')}
            active={location.pathname === '/tools'}
          />
        </Nav.Item>

        <Nav.Item>
          <NavLinkWithTooltip
            to="/settings"
            icon={faCog}
            text={t('sidebar.settings')}
            active={location.pathname === '/settings'}
          />
        </Nav.Item>
        
        {/* Link di amministrazione (visibile solo agli admin) */}
        {isAdmin && (
          <Nav.Item>
            <NavLinkWithTooltip
              to="/admin"
              icon={faShieldAlt}
              text={t('admin.title')}
              active={location.pathname === '/admin'}
            />
          </Nav.Item>
        )}
      </div>

      {/* Logout Button */}
      <div className="sidebar-footer">
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip className="sidebar-tooltip">
              {t('sidebar.logout')}
            </Tooltip>
          }
          delay={{ show: 200, hide: 0 }}
        >
          <Nav.Link 
            onClick={handleLogout}
            className="sidebar-link logout-link"
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span className="sidebar-text">{t('sidebar.logout')}</span>
          </Nav.Link>
        </OverlayTrigger>
      </div>
    </Nav>
  );
}

export default Sidebar;